import { useEffect, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/TextLayer.css'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import type { WizardState, Company, Renter } from './types'
import { toOere } from './types'
import { apiFetchRaw } from '../../../lib/api-client'

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString()


interface Props {
  state: WizardState
  company: Company | null
  renters?: Renter[]
  fetchKey: number
  zoom?: number
  onPdfLoad?: (url: string) => void
  onNumPages?: (n: number) => void
}

export function PdfPreviewPanel({
  state, company, renters = [], fetchKey, zoom = 1.0, onPdfLoad, onNumPages,
}: Props) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [numPages, setNumPages] = useState(0)
  const [pageWidth, setPageWidth] = useState(600)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const bodyRef = useRef<HTMLDivElement>(null)

  const renter = renters.find(r => r.id === state.renter_id)

  useEffect(() => {
    const el = bodyRef.current
    if (!el) return
    const obs = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width
      if (w) setPageWidth(Math.min(w - 80, 760))
    })
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  useEffect(() => {
    let cancelled = false
    const controller = new AbortController()

    async function run() {
      setLoading(true)
      setError(null)
      try {
        const body = {
          lease_display_type: state.lease_display_type || null,
          lease_display_other: state.lease_display_other || null,
          property_address: state.property_address || null,
          property_city: state.property_city || null,
          area_m2: state.area_m2 || null,
          commercial_area_m2: state.commercial_area_m2 || null,
          room_count: state.room_count || null,
          is_sublease: state.is_sublease,
          start_date: state.start_date || null,
          end_date: state.end_date || null,
          is_fixed_term: state.is_fixed_term,
          monthly_rent_oere: toOere(state.monthly_rent_dkk),
          payment_day: state.payment_day,
          payment_frequency: state.payment_frequency,
          deposit_oere: toOere(state.deposit_dkk),
          prepaid_rent_oere: toOere(state.prepaid_rent_dkk),
          first_payment_date: state.first_payment_date || null,
          first_payment_oere: toOere(state.first_payment_dkk),
          // Backstop: only send an a conto amount when its type/toggle is active,
          // so a stale value can never leak into the PDF even if state lags.
          aconto_heating_oere: state.heating_billing_type === 'aconto' ? toOere(state.aconto_heating_dkk) : 0,
          aconto_water_oere: state.water_billing_type === 'aconto' ? toOere(state.aconto_water_dkk) : 0,
          aconto_electricity_oere: state.el_billing_type === 'aconto' ? toOere(state.aconto_electricity_dkk) : 0,
          aconto_cooling_oere: state.cooling_billing_type === 'aconto' ? toOere(state.aconto_cooling_dkk) : 0,
          aconto_antenna_oere: state.antenna_type === 'faellesantenne' ? toOere(state.aconto_antenna_dkk) : 0,
          aconto_internet_oere: state.internet_type === 'udlejer' ? toOere(state.aconto_internet_dkk) : 0,
          beboer_amount_oere: state.beboerrepraesentation ? toOere(state.beboer_amount_dkk) : 0,
          extra_contributions: state.extra_contributions.map(c => ({ name: c.name, amount_oere: toOere(c.dkk) })),
          aconto_heating_index_start: state.aconto_heating_index_start || null,
          aconto_water_index_start: state.aconto_water_index_start || null,
          aconto_el_start_date: state.aconto_el_start_date || null,
          aconto_koling_start_date: state.aconto_koling_start_date || null,
          vand_individuelt_maaler: state.vand_individuelt_maaler,
          koling_individuelt_maaler: state.koling_individuelt_maaler,
          heating_provider: state.heating_provider || null,
          heating_type: state.heating_type || null,
          heating_type_other: state.heating_type_other || null,
          water_provider: state.water_provider || null,
          electricity_provider: state.electricity_provider || null,
          cooling_provider: state.cooling_provider || null,
          antenna_included: state.antenna_included,
          antenna_notes: state.antenna_notes || null,
          internet_included: state.internet_included,
          internet_provider: state.internet_provider || null,
          antenna_type: state.antenna_type || null,
          internet_type: state.internet_type || null,
          inspection_date: state.inspection_date || null,
          inspection_time: state.inspection_time || null,
          interior_maintenance: state.interior_maintenance,
          maintenance_notes: state.maintenance_notes || null,
          inventory_notes: state.inventory_notes || null,
          pets_allowed: state.pets_allowed,
          pets_notes: state.pets_notes || null,
          beboerrepraesentation: state.beboerrepraesentation,
          nettoprisindeks: state.nettoprisindeks,
          nettoprisindeks_base_year: state.nettoprisindeks_base_year || null,
          regulation_day: state.regulation_day || null,
          regulation_month: state.regulation_month || null,
          regulation_index_from: state.regulation_index_from || null,
          regulation_index_to: state.regulation_index_to || null,
          regulation_first_date: state.regulation_first_date || null,
          regulation_max_pct: state.regulation_max_pct || null,
          free_rent: state.free_rent,
          free_rent_reason: state.free_rent_reason || null,
          // Fields the signed PDF renders but the preview previously omitted —
          // kept here so forhåndsvis == the signed contract.
          unit_number: state.unit_number || null,
          payment_method: state.payment_method || null,
          deposit_payment_date: state.deposit_payment_date || null,
          prepaid_payment_date: state.prepaid_payment_date || null,
          engangsudgifter: state.engangsudgifter.map(e => ({ name: e.name, amount_oere: toOere(e.dkk) })),
          appliances: state.appliances,
          appliances_other: state.appliances_other,
          husorden: state.husorden || null,
          other_notes: state.other_notes || null,
          smoking_allowed: state.smoking_allowed,
          pets_option: state.pets_option || null,
          energy_label: state.energy_label || null,
          access_rights: state.access_rights,
          access_numbers: state.access_numbers,
          access_other: state.access_other || [],
          special_terms: state.special_terms || null,
          udlejer_navn: company?.name ?? null,
          udlejer_cvr: company?.cvr ?? null,
          udlejer_adresse: [company?.address, company?.postnr, company?.by].filter(Boolean).join(', ') || null,
          udlejer_tlf: company?.phone ?? null,
          udlejer_email: company?.email ?? null,
          lejer_navn: renter?.name ?? null,
          lejer_adresse: state.lejer_address || null,
          lejer_tlf: renter?.phone ?? null,
          lejer_email: renter?.email ?? null,
        }

        const res = await apiFetchRaw('/api/v1/contracts/pdf-preview', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          signal: controller.signal,
        })

        if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`)

        const blob = await res.blob()
        if (cancelled) return

        const newUrl = URL.createObjectURL(blob)
        setPdfUrl(prev => {
          if (prev) URL.revokeObjectURL(prev)
          return newUrl
        })
        onPdfLoad?.(newUrl)
      } catch (err) {
        if (!cancelled && (err as Error).name !== 'AbortError') {
          setError(err instanceof Error ? err.message : 'Fejl')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    run()
    return () => {
      cancelled = true
      controller.abort()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey])

  const scaledWidth = Math.round(pageWidth * zoom)

  return (
    <div
      ref={bodyRef}
      style={{ flex: 1, overflowY: 'auto', background: '#f0f0f0', padding: '28px 0 40px' }}
    >
      {loading && !pdfUrl && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          height: 300, color: '#6b7280', fontSize: 13,
        }}>
          Genererer PDF…
        </div>
      )}

      {error && (
        <div style={{ padding: 24, color: '#dc2626', fontSize: 12, textAlign: 'center' }}>
          {error}
        </div>
      )}

      {pdfUrl && (
        <Document
          file={pdfUrl}
          onLoadSuccess={({ numPages: n }) => {
            setNumPages(n)
            onNumPages?.(n)
          }}
          loading={null}
        >
          {Array.from({ length: numPages }, (_, i) => (
            <div
              key={i}
              id={`pdf-page-${i + 1}`}
              style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}
            >
              <div style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.25)', lineHeight: 0 }}>
                <Page
                  pageNumber={i + 1}
                  width={scaledWidth}
                  renderTextLayer={false}
                  renderAnnotationLayer={true}
                />
              </div>
            </div>
          ))}
        </Document>
      )}

      {/* Loading overlay when refreshing existing PDF */}
      {loading && pdfUrl && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24,
          background: 'rgba(0,0,0,0.7)', color: 'white',
          padding: '6px 14px', borderRadius: 20, fontSize: 12,
        }}>
          Opdaterer…
        </div>
      )}
    </div>
  )
}
